import React, { useEffect, useState } from "react";
import styles from "./AdminOutcomes.module.scss";
import {
  AdminDataService,
  LicenseHolder,
} from "./mock-data/admin-data.service";
import { ResponsiveBar } from "@nivo/bar";
import Button from "../../components/Button/Button";
import { useHistory } from "react-router-dom";

/**
 * Outcomes / Usage vs. Performance page
 *
 * This page shows the correlation between Team Nation usage and on-field results.
 * Currently uses mock outcome data since MaxPreps integration hasn't been built yet.
 * The license holder data (questions, users, etc.) is real.
 */
const AdminOutcomes: React.FC = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [topAccounts, setTopAccounts] = useState<LicenseHolder[]>([]);
  const [metrics, setMetrics] = useState<{
    totalAccounts: number;
    linkedAccounts: number;
  }>({ totalAccounts: 0, linkedAccounts: 0 });

  useEffect(() => {
    async function load() {
      await AdminDataService.initialize();
      const m = AdminDataService.getPlatformMetrics();
      setMetrics({
        totalAccounts: m.totalAccounts,
        // Mock: ~9% of accounts have linked MaxPreps data
        linkedAccounts: Math.round(m.totalAccounts * 0.091),
      });
      setTopAccounts(AdminDataService.getTopAccounts(10));
      setLoading(false);
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading outcomes data...</div>
      </div>
    );
  }

  // Mock usage tier data (would come from MaxPreps integration)
  const usageTierData = [
    { tier: "No Usage", winRate: 41, playoffRate: 18 },
    { tier: "Light (<1hr)", winRate: 45, playoffRate: 22 },
    { tier: "Moderate (1-3hr)", winRate: 52, playoffRate: 35 },
    { tier: "Heavy (3-5hr)", winRate: 67, playoffRate: 52 },
    { tier: "Power (5+hr)", winRate: 74, playoffRate: 68 },
  ];

  const featureImpactData = [
    {
      feature: "Competitive Play",
      winning: 45,
      losing: 8,
      lift: "+463%",
    },
    { feature: "Lessons", winning: 78, losing: 28, lift: "+179%" },
    {
      feature: "Flashcard Games",
      winning: 89,
      losing: 34,
      lift: "+162%",
    },
    { feature: "Playbook Only", winning: 98, losing: 95, lift: "+3%" },
  ];

  const nivoTheme = {
    textColor: "#ababb7",
    fontSize: 11,
    grid: { line: { stroke: "#424258", strokeWidth: 1 } },
    axis: {
      ticks: { text: { fill: "#ababb7", fontSize: 10 } },
      legend: { text: { fill: "#ababb7", fontSize: 12 } },
    },
    tooltip: {
      container: {
        background: "#282B3A",
        color: "#ffffff",
        fontSize: 12,
        borderRadius: 6,
        border: "1px solid #424258",
      },
    },
  };

  const coveragePct = (
    (metrics.linkedAccounts / metrics.totalAccounts) *
    100
  ).toFixed(1);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Usage vs Performance</h1>
          <p className={styles.subtitle}>
            How Team Nation drives on-field results
          </p>
        </div>
        <Button
          theme="secondary"
          size="small"
          onClick={() => history.push("/admin")}
        >
          Back to Licenses
        </Button>
      </div>

      {/* Hero Insight */}
      <div className={styles.heroInsight}>
        <div className={styles.heroHeadline}>
          Teams using Team Nation 3+ hours/week win 23% more games
        </div>
        <div className={styles.heroSub}>
          Based on analysis of {metrics.linkedAccounts.toLocaleString()} teams
          with linked MaxPreps data across the 2025 season
        </div>
        <div className={styles.heroCircles}>
          <div className={styles.heroCircle}>
            <div className={`${styles.heroCircleValue} ${styles.gold}`}>
              67%
            </div>
            <div className={styles.heroCircleLabel}>
              Win Rate
              <br />
              <strong>High Usage Teams</strong>
              <br />
              (3+ hrs/week)
            </div>
          </div>
          <div className={styles.vsText}>vs</div>
          <div className={styles.heroCircle}>
            <div className={styles.heroCircleValue}>44%</div>
            <div className={styles.heroCircleLabel}>
              Win Rate
              <br />
              <strong>Low Usage Teams</strong>
              <br />
              (&lt;1 hr/week)
            </div>
          </div>
        </div>
      </div>

      {/* Usage Tier Chart */}
      <div className={styles.card} style={{ marginBottom: 16 }}>
        <h3 className={styles.cardTitle}>
          Win Rate &amp; Playoff Rate by Usage Tier
        </h3>
        <div style={{ height: 300 }}>
          <ResponsiveBar
            data={usageTierData}
            theme={nivoTheme}
            indexBy="tier"
            keys={["winRate", "playoffRate"]}
            groupMode="grouped"
            margin={{ top: 20, right: 40, bottom: 60, left: 50 }}
            padding={0.3}
            innerPadding={4}
            colors={["#ffbc17", "#ffffff33"]}
            borderRadius={3}
            axisBottom={{
              tickRotation: -20,
            }}
            axisLeft={{
              format: (v) => `${v}%`,
            }}
            enableLabel={true}
            labelTextColor="#151724"
            label={(d) => `${d.value}%`}
            enableGridX={false}
            legends={[
              {
                dataFrom: "keys",
                anchor: "top-right",
                direction: "row",
                itemWidth: 120,
                itemHeight: 20,
                translateY: -20,
                itemTextColor: "#ababb7",
              },
            ]}
          />
        </div>
      </div>

      <div className={styles.cardGrid}>
        {/* Feature Impact */}
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Feature Impact on Winning</h3>
          <table className={styles.impactTable}>
            <thead>
              <tr>
                <th>Feature</th>
                <th>Winning Teams</th>
                <th>Losing Teams</th>
                <th>Lift</th>
              </tr>
            </thead>
            <tbody>
              {featureImpactData.map((row) => (
                <tr key={row.feature}>
                  <td className={styles.bold}>{row.feature}</td>
                  <td>{row.winning}%</td>
                  <td>{row.losing}%</td>
                  <td>
                    <span
                      className={styles.liftBadge}
                      style={{
                        background:
                          parseInt(row.lift) > 10
                            ? "rgba(76,175,80,0.15)"
                            : "rgba(171,171,183,0.15)",
                        color: parseInt(row.lift) > 10 ? "#4CAF50" : "#ababb7",
                      }}
                    >
                      {row.lift}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className={styles.footnote}>
            Competitive Play has the highest correlation — teams using it win at
            5.6x the rate of non-users.
          </p>
        </div>

        {/* Year Over Year */}
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Year-Over-Year Improvement</h3>
          <p className={styles.footnote} style={{ marginTop: 0 }}>
            For teams that have used Team Nation for 2+ seasons
          </p>
          {[
            { year: "Year 1 with TN", wins: "+1.8 wins", pct: 36 },
            { year: "Year 2 with TN", wins: "+2.6 wins", pct: 52 },
            { year: "Year 3 with TN", wins: "+3.1 wins", pct: 62 },
          ].map((row) => (
            <div key={row.year} className={styles.yoyRow}>
              <div className={styles.yoyLabel}>{row.year}</div>
              <div className={styles.yoyTrack}>
                <div
                  className={styles.yoyFill}
                  style={{ width: `${row.pct}%` }}
                >
                  {row.wins}
                </div>
              </div>
            </div>
          ))}

          {/* Data Coverage */}
          <div className={styles.coverageSection}>
            <h3 className={styles.cardTitle}>Data Coverage</h3>
            <p style={{ fontSize: 13, marginBottom: 8 }}>
              {metrics.linkedAccounts.toLocaleString()} of{" "}
              {metrics.totalAccounts.toLocaleString()} accounts have linked
              MaxPreps data <strong>({coveragePct}%)</strong>
            </p>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{ width: `${coveragePct}%` }}
              />
            </div>
            <p className={styles.footnote}>
              Accounts with linked data see 2x higher engagement
            </p>
            <Button
              theme="primary"
              size="small"
              onClick={() =>
                (window as any).dispatchToast?.({
                  type: "info",
                  message: "MaxPreps integration coming soon",
                })
              }
            >
              Increase Coverage
            </Button>
          </div>
        </div>
      </div>

      {/* Top Performing Teams */}
      <div className={styles.card}>
        <h3 className={styles.cardTitle}>
          Top Performing Team Nation Teams (2025 Season)
        </h3>
        <table className={styles.miniTable}>
          <thead>
            <tr>
              <th>#</th>
              <th>Team</th>
              <th>Coach</th>
              <th>Tier</th>
              <th>Questions</th>
              <th>Health</th>
            </tr>
          </thead>
          <tbody>
            {topAccounts.map((a, i) => (
              <tr
                key={a.email}
                onClick={() =>
                  history.push(`/admin/customer/${encodeURIComponent(a.email)}`)
                }
                style={{ cursor: "pointer" }}
              >
                <td>{i + 1}</td>
                <td className={styles.bold}>
                  {a.teamName !== "-" ? a.teamName : a.owner}
                </td>
                <td>{a.owner}</td>
                <td>
                  <span className={styles.tierBadge}>{a.tier}</span>
                </td>
                <td>{a.questions.toLocaleString()}</td>
                <td>
                  <span
                    className={`${styles.healthPill} ${
                      a.healthLabel === "Healthy"
                        ? styles.green
                        : a.healthLabel === "At Risk"
                        ? styles.yellow
                        : styles.red
                    }`}
                  >
                    {a.healthScore}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminOutcomes;
